import { create } from 'zustand';
import { getComponentStore } from './componentStoreRegistry';
import { normalizeV2ToV1 } from './enumNormalization';
import { migrateAccessPolicy } from './accessPolicyMigration';

/**
 * Factory for creating standardised component stores.
 *
 * Every component store shares a common interface consumed by:
 *   - prefillComponentStores (calls setComponentData)
 *   - buildComponentSavePayload (calls getTemplate)
 *   - storeWriteGuard (reads data, calls getTemplate/initialiseEmpty)
 *   - ComponentInitializationUtil (calls initialiseEmpty + getTemplate)
 *
 * This factory provides that interface automatically and lets each component
 * define only its unique data shape and custom actions.
 *
 * @param {object} config
 * @param {string}   config.type         - Component type (e.g. 'trigger_from_external_app')
 * @param {string}   config.endpoint     - Backend endpoint name
 * @param {string}   config.registryKey  - Key for componentStoreRegistry
 * @param {function} config.initData     - () => initialDataObject. Called on every reset.
 * @param {function} [config.onLoadData] - (rawData, component) => data.
 *   Custom transform when loading from backend. If omitted, rawData is spread
 *   over initData() defaults: { ...initData(), ...rawData }.
 * @param {function} [config.extraState]   - (set, get) => ({...}) additional state fields
 * @param {function} [config.extraActions] - (set, get) => ({...}) additional actions
 */
export function createComponentStore(config) {
  const {
    type,
    endpoint,
    registryKey,
    initData,
    onLoadData,
    extraState,
    extraActions,
  } = config;

  const storeFactory = () => create((set, get) => {
    const extraStateObj = extraState ? extraState(set, get) : {};
    const extraActionsObj = extraActions ? extraActions(set, get) : {};

    // Strip functions from extraState so initialiseEmpty only resets plain values
    const extraStateDefaults = {};
    for (const [k, v] of Object.entries(extraStateObj)) {
      if (typeof v !== 'function') extraStateDefaults[k] = v;
    }

    return {
      // ---- Base state ----
      loaded: false,
      endpoint,
      componentId: '',
      name: '',
      type,
      lastModified: '',
      data: initData(),

      // ---- Component-specific state ----
      ...extraStateObj,

      // ---- Base actions ----

      initialiseEmpty: () => {
        set({
          loaded: false,
          componentId: '',
          name: '',
          lastModified: '',
          data: initData(),
          ...extraStateDefaults,
        });
      },

      /**
       * Standard setter — called by prefillComponentStores.
       * Accepts { componentId, type, componentData: { name, data, lastModified } }
       */
      setComponentData: (component) => {
        const cd = component.componentData;
        const updates = {
          componentId: component.componentId,
          loaded: true,
        };
        if (component.type) updates.type = component.type;

        if (cd) {
          const isV2 = cd.schemaVersion >= 2;

          let rawData;
          if (isV2) {
            const { schemaVersion, name, lastModified, ...rest } = cd;
            rawData = rest;
          } else if (cd.data != null) {
            rawData = typeof cd.data === 'object' ? cd.data : {};
          } else {
            const { name, lastModified, ...rest } = cd;
            rawData = rest;
          }

          rawData = normalizeV2ToV1(component.type || type, rawData);
          rawData = migrateAccessPolicy(component.type || type, rawData);

          updates.data = onLoadData
            ? onLoadData(rawData, component)
            : { ...initData(), ...rawData };
          updates.name = cd.name || '';
          updates.lastModified = cd.lastModified || '';
        } else {
          updates.data = initData();
          updates.name = '';
          updates.lastModified = '';
        }

        set(updates);
      },

      setTemplateData: (data) => set({ data }),

      setName: (name) => set({ name }),

      setLastModified: (lastModified) => set({ lastModified }),

      getTemplate: () => {
        const s = get();
        return {
          componentId: s.componentId,
          name: s.name,
          type: s.type,
          lastModified: s.lastModified,
          data: JSON.parse(JSON.stringify(s.data)),
        };
      },

      // ---- Component-specific actions ----
      ...extraActionsObj,
    };
  });

  function getStore(componentId) {
    return getComponentStore(registryKey, componentId, storeFactory);
  }

  const defaultStore = storeFactory();

  return { createStore: storeFactory, getStore, defaultStore };
}
